/* import __COLOCATED_TEMPLATE__ from './buyer-profile-form.hbs'; */
import { computed } from '@ember/object';
import { or, notEmpty } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { handleFormErrorOrModal } from 'sh-www/utils/handles-errors';
import { phoneValidator, slugValidator } from '../../utils/validators';

/** @type {string[]} */
const FORM_INPUTS = [
  'first_name',
  'middle_name',
  'last_name',
  'description',
  'phone',
  'slug',
];

/** @type {string[][]} */
const FORM_TUPLES = [
  ['first_name', 'firstNameValidator'],
  ['middle_name', 'middleNameValidator'],
  ['last_name', 'lastNameValidator'],
  ['description', 'descriptionValidator'],
  ['phone', 'phoneValidator'],
  ['photo', 'photoValidator'],
  ['slug', 'slugValidator'],
];

/** @module */
const SettingsBuyerProfileForm = Component.extend({
  /** @inheritDoc */
  tagName: '',

  errorTracker: service('error-tracker'),

  /** @type {DS.Model} */
  user: null,

  /** @type {DS.Model} */
  photo: null,

  /** @type {string} */
  first_name: '',

  /** @type {string} */
  middle_name: '',

  /** @type {string} */
  last_name: '',

  /** @type {string} */
  description: '',

  /** @type {string} */
  slug: '',

  /** @type {string} */
  phone: '',

  /** @type {string} */
  previewImageUrl: '',

  /** @type {boolean} */
  uploadingPhoto: false,

  /** @type {boolean} */
  saving: false,

  /** @type {function(File): Promise<any>} */
  uploadPhoto: undefined,

  /** @type {Function} */
  deleteAccount: () => {},

  /** @type {function(Object): Promise<any>} */
  updateUser: undefined,

  /** @type {function(): string} */
  getDefaultUserPhotoUrl: undefined,

  /** @type {function(string, string): string} */
  getPhotoUrl: undefined,

  /** @type {Ember.ComputedProperty} */
  disableForm: or('saving', 'uploadingPhoto'),

  /** @type {Ember.ComputedProperty} */
  firstNameValidator: computed('first_name', function() {
    if (!this.first_name) return 'First name is a required field';
  }),

  /** @type {Ember.ComputedProperty} */
  middleNameValidator: computed('middle_name', function() {
    return undefined;
  }),

  /** @type {Ember.ComputedProperty} */
  lastNameValidator: computed('last_name', function() {
    if (!this.last_name) return 'Last name is a required field';
  }),

  /** @type {Ember.ComputedProperty} */
  descriptionValidator: computed('description', function() {
    return undefined;
  }),

  /** @type {Ember.ComputedProperty} */
  phoneValidator: computed('phone', function() {
    return phoneValidator(this.get('phone'));
  }),

  /** @type {Ember.ComputedProperty} */
  photoValidator: computed('previewImageUrl', function() {
    if (!this.get('previewImageUrl')) return 'You must upload a valid photo';
  }),

  /** @type {Ember.ComputedProperty} */
  slugValidator: computed('slug', function() {
    return slugValidator(this.get('slug'));
  }),

  /** @type {Boolean} */
  isPendingDeletion: notEmpty('user.deletionRequestedAt'),

  /** @inheritDoc */
  init() {
    this._super(...arguments);

    const {
      user,
      getDefaultUserPhotoUrl,
    } = this.getProperties([
      'user',
      'getDefaultUserPhotoUrl',
    ]);

    const props = {
      errors: { },
      first_name: user.get('firstName') || '',
      middle_name: user.get('middleName') || '',
      last_name: user.get('lastName') || '',
      description: user.get('description') || '',
      phone: user.get('phone') || '',
      previewImageUrl: getDefaultUserPhotoUrl(),
      slug: user.get('slug') || '',
    };

    this.setProperties(props);
  },

  /** @inheritDoc */
  actions: {
    /**
     * @param {string} field
     * @param {string} value
     */
    updateField(field, value) {
      this.setProperties({ [field]: value, [`errors.${field}`]: undefined });
    },

    /**
     * @param {string} field
     * @param {string} validator
     */
    validateField(field, validator) {
      const error = this.get(validator);

      if (error) {
        this.set(`errors.${field}`, error);
      }
    },

    /**
     * @param {File} file
     */
    handleImageUpload(file) {
      this.setProperties({ uploadingPhoto: true, 'errors.photo': undefined });

      this.get('uploadPhoto')(file).then((photo) => {
        this.setProperties({ photo, previewImageUrl: this.get('getPhotoUrl')(photo) });
      }).catch((e) => {
        this.get('errorTracker').error(e);
        this.set('errors.photo', 'There was an error uploading your photo. Please try again');
      }).finally(() => {
        this.set('uploadingPhoto', false);
      });
    },

    /**
     *
     */
    handleSubmit() {
      if (this.get('disableForm') || !this._validateForm()) {
        return;
      }

      this.set('saving', true);

      this.get('updateUser')(this.getProperties([...FORM_INPUTS, 'photo'])).catch((e) => {
        handleFormErrorOrModal(
          this,
          e,
          FORM_INPUTS,
          false,
          'There was an error saving your profile. Please refresh and try again.',
          'settingsProfileFail -- error updating user profile.' + JSON.stringify(e)
        );
      }).finally(() => {
        this.set('saving', false);
      });
    },

    deleteAccount() {
      this.get('deleteAccount')();
    },
  },

  /**
   * @returns {boolean}
   * @private
   */
  _validateForm() {
    const [errors, hasError] = FORM_TUPLES.reduce(([errors, hasError], [key, validator]) => {
      const err = this.get(validator);

      if (err) {
        errors[key] = err;
        hasError = true;
      }

      return [errors, hasError];
    }, [{ }, false]);

    this.set('errors', errors);
    return !hasError;
  },
});

export default SettingsBuyerProfileForm;
